// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useMemo } from 'react';

import type { ProviderAccessPageContext, ProviderRejectionRequest } from '../types/providerAccess';
import type { WizardStep } from '../types/wizard';
import { WizardLayout } from './WizardLayout';
import { WizardHeader } from './WizardHeader';
import { WizardSidebar } from './WizardSidebar';
import { WizardStepContent } from './WizardStepContent';
import { StepProviderDetails } from './steps/StepProviderDetails';
import { RejectionPanel } from './wizard/RejectionPanel';

export interface ProviderCoAWizardProps {
  context: ProviderAccessPageContext;
  submitting: boolean;
  onAccept: (formData: Record<string, unknown>) => Promise<boolean>;
  onReject: (request: ProviderRejectionRequest) => Promise<boolean>;
}

const COA_STEPS: WizardStep[] = [
  { id: 'loa-validation', name: 'Letter of Authority', index: 0, status: 'incomplete', savedAt: null },
];

/**
 * Single-step Change of Agency form for the provider Open Access page.
 *
 * Renders when {@code context.documentType === 'COA'}. Uses the same
 * WizardLayout shell as the LOA wizard (header, sidebar, content card)
 * but with a single accept-or-reject step instead of a multi-step wizard.
 */
export function ProviderCoAWizard({
  context,
  submitting,
  onAccept,
  onReject,
}: ProviderCoAWizardProps) {
  const [coaAccepted, setCoaAccepted] = useState<boolean | undefined>(undefined);
  const [providerData, setProviderData] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const title = context.clientName
    ? `Change of Agency for ${context.clientName}`
    : 'Change of Agency';

  const categoryLabel = context.policyTypeCategory
    ? context.policyTypeCategory.charAt(0).toUpperCase() + context.policyTypeCategory.slice(1).toLowerCase()
    : 'policy';

  const description = (() => {
    if (context.advisorName && context.brokerageName && context.clientName) {
      return `${context.advisorName} at ${context.brokerageName} is requesting change of agency for ${context.clientName}. You can view the signed Change of Agency Letter in the email sent to you by ${context.brokerageName}.`;
    }
    if (context.brokerageName) {
      return `${context.brokerageName} is requesting change of agency for ${categoryLabel.toLowerCase()} details. You can view the signed Change of Agency Letter in the email sent to you by ${context.brokerageName}.`;
    }
    return 'Please review the Change of Agency sent to you via email.';
  })();

  // Map LOA_EXPIRED display text to CoA equivalent
  const coaRejectionReasons = useMemo(() =>
    (context.rejectionReasons ?? []).map(r => ({
      ...r,
      displayText: r.code === 'LOA_EXPIRED'
        ? r.displayText.replace(/LOA/gi, 'CoA')
        : r.displayText,
    })),
    [context.rejectionReasons]
  );

  const handleFieldChange = useCallback((key: string, value: unknown) => {
    setProviderData(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleClearFieldError = useCallback((key: string) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const validateProviderFields = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!providerData.providerContactName?.toString().trim()) {
      errors.providerContactName = 'Name of provider is required';
    }
    if (!providerData.completedByName?.toString().trim()) {
      errors.completedByName = 'Completed by is required';
    }
    const email = providerData.completedByEmail?.toString().trim() || '';
    if (!email) {
      errors.completedByEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.completedByEmail = 'Please enter a valid email address';
    }
    return errors;
  }, [providerData]);

  const handleAccept = useCallback(async () => {
    const errors = validateProviderFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    await onAccept({
      providerContactName: providerData.providerContactName,
      completedByName: providerData.completedByName,
      completedByEmail: providerData.completedByEmail,
    });
  }, [validateProviderFields, onAccept, providerData]);

  return (
    <WizardLayout context={context}>
      <WizardHeader
        context={context}
        title={title}
        lastSavedAt={null}
      />
      <WizardSidebar
        steps={COA_STEPS}
        currentStepId="loa-validation"
        onStepClick={() => {}}
        canNavigateTo={() => true}
      />
      <WizardStepContent>
        <div className="flex flex-col" data-testid="provider-coa-wizard">
          <h2 className="text-black font-sans text-h5 font-normal leading-normal not-italic">
            Change of Agency
          </h2>

          <div className="mt-sm text-black font-sans text-body-02 font-normal leading-normal not-italic">
            {description}
          </div>

          <div className="mt-lg text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Is the Change of Agency accepted?
          </div>

          <div className="mt-md flex flex-col gap-md">
            <label className="flex items-center gap-[12px] cursor-pointer" onClick={() => setCoaAccepted(true)}>
              <div className="w-[16px] h-[16px] rounded-full border border-purple-primary flex items-center justify-center bg-white shrink-0">
                {coaAccepted === true && (
                  <div className="w-[8px] h-[8px] rounded-full bg-purple-primary" />
                )}
              </div>
              <div className="text-black font-sans text-body-02 font-normal leading-normal not-italic">Yes</div>
            </label>

            <label className="flex items-center gap-[12px] cursor-pointer" onClick={() => setCoaAccepted(false)}>
              <div className="w-[16px] h-[16px] rounded-full border border-purple-primary flex items-center justify-center bg-white shrink-0">
                {coaAccepted === false && (
                  <div className="w-[8px] h-[8px] rounded-full bg-purple-primary" />
                )}
              </div>
              <div className="text-black font-sans text-body-02 font-normal leading-normal not-italic">No</div>
            </label>
          </div>

          {coaAccepted === true && (
            <>
              <div className="mt-lg w-full h-[1px] bg-[#E1E1E1]" />

              <div className="mt-lg">
                <StepProviderDetails
                  data={providerData}
                  onFieldChange={handleFieldChange}
                  fieldErrors={fieldErrors}
                  onClearFieldError={handleClearFieldError}
                />
              </div>

              <div className="flex justify-end mt-lg">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleAccept}
                  className="h-[48px] px-xl rounded-button bg-purple-primary border-none cursor-pointer transition-colors hover:bg-[#1E0649] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="text-white font-sans text-body-02 font-medium leading-normal not-italic capitalize">
                    {submitting ? 'Submitting...' : 'Accept Change Of Agency'}
                  </div>
                </button>
              </div>
            </>
          )}

          {coaAccepted === false && (
            <RejectionPanel
              rejectionReasons={coaRejectionReasons}
              submitting={submitting}
              onReject={onReject}
              submitLabel="Reject Change Of Agency"
            />
          )}
        </div>
      </WizardStepContent>
    </WizardLayout>
  );
}
